import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import { API_ENDPOINTS, API_BASE_URL } from '../../../../utils/config';
import { X, Plus, Trash2, Calendar } from 'lucide-react';

const TaxInvoiceForm = ({ isOpen, onClose, onSave, invoiceType = 'domestic-invoice' }) => {
  // Helper to normalize company logo URL
  const getCompanyLogoUrl = (profileData) => {
    if (!profileData) return null;

    const rawLogo = profileData.company_logo || profileData.logo;
    if (!rawLogo) return null;

    const logoUrl = String(rawLogo);

    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
      return logoUrl;
    }

    const baseUrl = (API_BASE_URL || '').replace('/api', '');

    if (logoUrl.startsWith('/')) {
      return `${baseUrl}${logoUrl}`;
    }

    return `${baseUrl}/${logoUrl}`;
  };

  // Document type configuration
  const documentConfig = useMemo(() => {
    const configs = {
      'quote': {
        title: 'Create Quote',
        banner: 'QUOTE',
        numberLabel: 'Quote No',
        dateLabel: 'Quote Date',
        showDueDate: true,
        submitText: 'Create Quote',
        prefix: 'QUO'
      },
      'estimate': {
        title: 'Create Estimate',
        banner: 'ESTIMATE',
        numberLabel: 'Estimate No',
        dateLabel: 'Estimate Date',
        showDueDate: false,
        submitText: 'Create Estimate',
        prefix: 'EST'
      },
      'domestic-invoice': {
        title: 'Create Tax Invoice',
        banner: 'TAX INVOICE',
        numberLabel: 'Invoice No',
        dateLabel: 'Invoice Date',
        showDueDate: true,
        submitText: 'Create Invoice',
        prefix: 'INV'
      },
      'international-invoice': {
        title: 'Create Tax Invoice',
        banner: 'TAX INVOICE',
        numberLabel: 'Invoice No',
        dateLabel: 'Invoice Date',
        showDueDate: true,
        submitText: 'Create Invoice',
        prefix: 'INV'
      },
      'bill-of-supply': {
        title: 'Create Bill of Supply',
        banner: 'BILL OF SUPPLY',
        numberLabel: 'Bill No',
        dateLabel: 'Bill Date',
        showDueDate: true,
        submitText: 'Create Bill of Supply',
        prefix: 'BOS'
      },
      'credit-note': {
        title: 'Create Credit Note',
        banner: 'CREDIT NOTE',
        numberLabel: 'Credit Note No',
        dateLabel: 'Credit Note Date',
        showDueDate: true,
        submitText: 'Create Credit Note',
        prefix: 'CN'
      },
      'receipt': {
        title: 'Create Receipt',
        banner: 'RECEIPT',
        numberLabel: 'Receipt No',
        dateLabel: 'Receipt Date',
        showDueDate: false,
        submitText: 'Create Receipt',
        prefix: 'REC'
      },
      'delivery-challan': {
        title: 'Create Delivery Challan',
        banner: 'DELIVERY CHALLAN',
        numberLabel: 'Challan No',
        dateLabel: 'Challan Date',
        showDueDate: true,
        submitText: 'Create Delivery Challan',
        prefix: 'DC'
      },
      'purchase-invoice': {
        title: 'Create Purchase Invoice',
        banner: 'PURCHASE INVOICE',
        numberLabel: 'Invoice No',
        dateLabel: 'Invoice Date',
        showDueDate: true,
        submitText: 'Create Purchase Invoice',
        prefix: 'PINV'
      },
      'purchase-order': {
        title: 'Create Purchase Order',
        banner: 'PURCHASE ORDER',
        numberLabel: 'Order No',
        dateLabel: 'Order Date',
        showDueDate: true,
        submitText: 'Create Purchase Order',
        prefix: 'PO'
      },
      'debit-note': {
        title: 'Create Debit Note',
        banner: 'DEBIT NOTE',
        numberLabel: 'Debit Note No',
        dateLabel: 'Debit Note Date',
        showDueDate: true,
        submitText: 'Create Debit Note',
        prefix: 'DN'
      },
      'voucher-payment': {
        title: 'Create Voucher Payment',
        banner: 'VOUCHER PAYMENT',
        numberLabel: 'Voucher No',
        dateLabel: 'Payment Date',
        showDueDate: false,
        submitText: 'Create Voucher Payment',
        prefix: 'VP'
      },
      'salary-payment': {
        title: 'Create Salary Payment',
        banner: 'SALARY PAYMENT',
        numberLabel: 'Voucher No',
        dateLabel: 'Payment Date',
        showDueDate: false,
        submitText: 'Create Salary Payment',
        prefix: 'SAL'
      }
    };
    return configs[invoiceType] || configs['domestic-invoice'];
  }, [invoiceType]);
  const [formData, setFormData] = useState({
    invoice_number: '',
    invoice_date: '',
    due_date: '',
    customer: '',
    invoice: '',
    payment_type: 'bank_transfer',
    status: 'draft',
    customer_name: '',
    customer_company: '',
    customer_address: '',
    customer_gstin: '',
    customer_pan: '',
    items: [
      {
        id: 1,
        sac_code: '',
        hsn_code: '',
        item_type: 'goods', // 'goods' | 'service'
        description: '',
        quantity: 1,
        amount: 0
      }
    ],
    total_taxable_value: 0,
    igst_rate: 0,
    igst_amount: 0,
    cgst_rate: 0,
    cgst_amount: 0,
    sgst_rate: 0,
    sgst_amount: 0,
    total_invoice_value: 0,
    notes: '',
    terms: '',
    reference_number: '',
    bank_account_name: '',
    bank_name: '',
    bank_account_no: '',
    bank_ifsc: '',
    bank_branch: ''
  });

  const [customers, setCustomers] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [contactType, setContactType] = useState(''); // 'customer', 'client', or 'supplier'
  const [salesCatalogItems, setSalesCatalogItems] = useState([]);
  const [purchasedItems, setPurchasedItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [companyProfile, setCompanyProfile] = useState(null);
  const [salaryForm, setSalaryForm] = useState({
    employee: '',
    payment_date: '',
    salary_month: '',
    payment_mode: 'bank_transfer',
    gross_salary: '',
    basic_salary: '',
    house_rent_allowance: '',
    transport_allowance: '',
    medical_allowance: '',
    income_tax: '',
    provident_fund: '',
    professional_tax: '',
    net_pay: '',
    account_number: '',
    ifsc_code: '',
    notes: '',
    status: 'draft'
  });

  // Extract first 2 digits (state code) from GST number. GSTIN format: 2-digit state + 10-digit PAN + ...
  const getGstStateCode = (gstin) => {
    if (!gstin || typeof gstin !== 'string') return null;
    const cleaned = String(gstin).replace(/\s/g, '').toUpperCase();
    if (cleaned.length >= 2 && /^\d{2}/.test(cleaned)) return cleaned.substring(0, 2);
    return null;
  };

  // Use CGST+SGST only when company and customer/client have same state code. Otherwise IGST.
  const shouldUseCgstSgst = (companyGstin, customerGstin) => {
    if (invoiceType === 'international-invoice') return false; // Always IGST for international
    const companyState = getGstStateCode(companyGstin);
    const customerState = getGstStateCode(customerGstin);
    if (!companyState || !customerState) return false; // Missing GST → use IGST
    return companyState === customerState;
  };

  const useSuppliers = ['purchase-invoice', 'purchase-order', 'debit-note', 'voucher-payment'].includes(invoiceType);
  const useSalaryPayment = invoiceType === 'salary-payment';

  // Helpers for money formatting & validation
  const toMoney = (value) => {
    const num = Number(value || 0);
    if (Number.isNaN(num)) return '0.00';
    return num.toFixed(2);
  };

  const exceedsMaxDigits = (moneyString) => {
    // Max 15 total digits (excluding decimal point)
    if (!moneyString) return false;
    const digitsOnly = moneyString.replace('.', '');
    return digitsOnly.length > 15;
  };

  useEffect(() => {
    if (isOpen) {
      if (useSalaryPayment) {
        fetchEmployees();
      } else if (useSuppliers) {
        fetchSuppliers();
      } else {
        fetchCustomers();
        fetchClients();
      }
      if (!useSalaryPayment) {
        fetchSalesCatalogItems();
        if (useSuppliers) fetchPurchasedItems();
      }
      fetchCompanyProfile();
      // Set default dates
      const today = new Date();
      const nextMonth = new Date(today);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      
      // For quotes, due_date is required, so always set it
      const shouldShowDueDate = documentConfig.showDueDate || invoiceType === 'quote';
      
      const todayStr = today.toISOString().split('T')[0];
      const salaryMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
      setFormData(prev => ({
        ...prev,
        invoice_date: todayStr,
        due_date: shouldShowDueDate ? nextMonth.toISOString().split('T')[0] : '',
        invoice_number: `${documentConfig.prefix}-${today.getFullYear()}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`
      }));
      if (useSalaryPayment) {
        setSalaryForm({
          employee: '',
          payment_date: todayStr,
          salary_month: salaryMonth,
          payment_mode: 'bank_transfer',
          gross_salary: '',
          basic_salary: '',
          house_rent_allowance: '',
          transport_allowance: '',
          medical_allowance: '',
          income_tax: '',
          provident_fund: '',
          professional_tax: '',
          net_pay: '',
          account_number: '',
          ifsc_code: '',
          notes: '',
          status: 'draft'
        });
      }
      // Reset contact type when form opens
      if (useSalaryPayment) {
        setContactType('employee');
      } else if (useSuppliers) {
        setContactType('supplier');
      } else if (invoiceType === 'quote' || invoiceType === 'receipt') {
        setContactType('customer');
      } else {
        setContactType('');
      }
    }
  }, [isOpen, invoiceType, documentConfig, useSuppliers, useSalaryPayment]);

  const fetchCompanyProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(API_ENDPOINTS.COMPANY_PROFILE_LIST, { headers });

      const profiles = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || []);

      if (!profiles.length) {
        setCompanyProfile(null);
        return;
      }

      const profileData = profiles[0];

      setCompanyProfile({
        name: profileData.company_name || profileData.companyName || '',
        address: profileData.address || '',
        gstin: profileData.gstin || profileData.gst_in || '',
        phone: profileData.phone_number || profileData.phone || '',
        email: profileData.email || '',
        accountName: profileData.account_name || profileData.accountName || '',
        bankName: profileData.bank_name || profileData.bankName || '',
        accountNo: profileData.account_no || profileData.accountNo || profileData.account_number || '',
        ifscCode: profileData.ifsc_code || profileData.ifscCode || '',
        branch: profileData.branch || '',
        logoUrl: getCompanyLogoUrl(profileData)
      });
    } catch (error) {
      console.error('Failed to fetch company profile:', error);
      setCompanyProfile(null);
    }
  };

  // When company profile is loaded, prefill bank details and keep fields editable
  useEffect(() => {
    if (!companyProfile || !isOpen) return;

    setFormData((prev) => ({
      ...prev,
      bank_account_name: prev.bank_account_name || companyProfile.accountName || '',
      bank_name: prev.bank_name || companyProfile.bankName || '',
      bank_account_no: prev.bank_account_no || companyProfile.accountNo || '',
      bank_ifsc: prev.bank_ifsc || companyProfile.ifscCode || '',
      bank_branch: prev.bank_branch || companyProfile.branch || ''
    }));
  }, [companyProfile, isOpen]);

  // Recalculate taxes when company or customer GST changes (state code determines CGST/SGST vs IGST)
  useEffect(() => {
    if (!isOpen) return;
    const hasTaxableDoc = ['domestic-invoice', 'international-invoice', 'bill-of-supply'].includes(invoiceType);
    if (!hasTaxableDoc || !formData.items?.length) return;
    calculateTotals(formData.items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.customer_gstin, companyProfile?.gstin, isOpen, invoiceType]);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };
      const response = await axios.get(API_ENDPOINTS.CUSTOMERS_LIST, { headers });
      const customersData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.results || []);
      setCustomers(customersData);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };
      const response = await axios.get(API_ENDPOINTS.CLIENTS_LIST, { headers });
      const clientsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.results || response.data?.data || response.data?.clients || []);
      setClients(clientsData);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };
      const response = await axios.get(API_ENDPOINTS.EMPLOYEES_LIST, { headers });
      const employeesData = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || response.data?.data || []);
      setEmployees(employeesData);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const getEmployeeDisplayName = (employee) => {
    if (!employee) return '';
    const empData = employee?.employee_data || {};
    const basicInfo = empData?.basic_information || {};
    return empData?.employee_name ||
           basicInfo?.full_name ||
           basicInfo?.employee_name ||
           employee.employee_name ||
           employee.name ||
           employee.full_name ||
           `Employee ${employee?.id ?? ''}`;
  };

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };
      const response = await axios.get(API_ENDPOINTS.SUPPLIERS_LIST, { headers });
      let suppliersData = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || response.data?.data || []);

      // Hydrate suppliers that are missing basic_information (list API returns partial data)
      const needsHydration = suppliersData.some(s => !s?.supplier_data?.basic_information);
      if (needsHydration) {
        suppliersData = await Promise.all(
          suppliersData.map(async (supplier) => {
            if (supplier?.supplier_data?.basic_information) return supplier;
            try {
              const detail = await axios.get(API_ENDPOINTS.SUPPLIERS_DETAIL(supplier.id), { headers });
              return { ...supplier, ...detail.data, supplier_data: detail.data?.supplier_data || supplier.supplier_data };
            } catch {
              return supplier;
            }
          })
        );
      }

      setSuppliers(suppliersData);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const fetchPurchasedItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Accept': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const response = await axios.get(API_ENDPOINTS.PURCHASED_ITEMS_LIST, { headers });
      const items = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      setPurchasedItems(items);
    } catch {
      setPurchasedItems([]);
    }
  };

  const fetchSalesCatalogItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };
      const response = await axios.get(API_ENDPOINTS.SALES_CATALOG_LIST, { headers });
      const items = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.results || []);
      setSalesCatalogItems(items);
    } catch (error) {
      console.error('Failed to fetch sales catalog items:', error);
    }
  };

  const getCustomerDisplayName = (customer) => {
    const basic = customer?.customer_data?.basic_information || customer?.customer_data || {};
    const candidates = [
      basic.customer_name,
      basic.display_name,
      customer.customer_name,
      customer.customer_display_name,
      customer.name,
      customer.company_name
    ];
    const picked = candidates.find((n) => n && String(n).trim().length > 0);
    return picked || `Customer ${customer?.id ?? ''}`.trim();
  };

  const formatAddress = (...parts) => parts
    .flat()
    .filter(Boolean)
    .map((part) => String(part).trim())
    .filter((part) => part.length > 0)
    .join(', ');

  const extractCustomerDetails = (customer) => {
    const customerData = customer?.customer_data || {};
    const basic = customerData.basic_information || customerData;
    const taxDetails = customerData.tax_details || {};
    const billing = customerData.billing_address || {};
    const shipping = customerData.shipping_address || {};

    // Prefer structured billing address parts; fall back to shipping or flat strings
    const billingAddress = formatAddress(
      billing.address_line_1,
      billing.address_line_2,
      billing.city || customerData.billing_city,
      billing.state || customerData.billing_state,
      billing.postal_code || customerData.billing_postal_code || customerData.billing_pin || customerData.postal_code,
      billing.country || customerData.billing_country,
      billing.address || customerData.billing_address
    );

    const shippingAddress = formatAddress(
      shipping.address_line_1 || customerData.shipping_address_line_1,
      shipping.address_line_2 || customerData.shipping_address_line_2,
      shipping.city || customerData.shipping_city,
      shipping.state || customerData.shipping_state,
      shipping.postal_code || customerData.shipping_postal_code,
      shipping.country || customerData.shipping_country,
      shipping.address || customerData.shipping_address
    );

    return {
      name: getCustomerDisplayName(customer),
      company:
        basic.business_name ||
        basic.company_name ||
        customer.customer_company ||
        customer.company_name ||
        '',
      address: billingAddress || shippingAddress || '',
      gstin:
        basic.customer_gstin ||
        basic.gstin ||
        customerData.customer_gstin ||
        customerData.gstin ||
        '',
      pan: taxDetails.pan || customerData.pan || ''
    };
  };

  const extractClientDetails = (client) => {
    if (!client) return {};
    const tax = client.tax_details || {};
    const gstin =
      client.gst_number ||
      client.gstin ||
      client.gst_in ||
      tax.gstin ||
      '';
    const companyName =
      client.company_name ||
      client.business_name ||
      client.name ||
      '';
    const addressPieces = [
      client.address,
      client.address_line_1,
      client.address_line_2,
      client.city,
      client.state,
      client.postal_code || client.pincode,
      client.country
    ];
    return {
      name: client.name || client.client_name || client.company_name || '',
      company: companyName || client.name || '',
      address: formatAddress(addressPieces),
      gstin,
      pan: client.pan || tax.pan || ''
    };
  };

  const getSupplierDisplayName = (supplier) => {
    if (!supplier) return '';
    const basic = supplier.supplier_data?.basic_information || {};
    return basic.supplier_name ||
           basic.business_name ||
           basic.display_name ||
           `Supplier ${supplier?.id ?? ''}`;
  };

  const extractSupplierDetails = (supplier) => {
    if (!supplier) return { company: '', address: '', gstin: '', pan: '' };
    const supplierData = supplier?.supplier_data || {};
    const basic = supplierData.basic_information || {};
    const taxDetails = supplierData.tax_details || {};
    const billing = supplierData.billing_address || {};
    return {
      company: basic.business_name || basic.gst_registered_name || '',
      address: formatAddress([
        billing.address_line_1,
        billing.address_line_2,
        billing.city,
        billing.state,
        billing.postal_code,
        billing.country
      ]),
      gstin: basic.supplier_gstin || taxDetails.pan || '',
      pan: taxDetails.pan || ''
    };
  };

  const handleContactTypeChange = (type) => {
    setContactType(type);
    // Reset customer selection when type changes
    setFormData(prev => ({
      ...prev,
      customer: '',
      customer_name: '',
      customer_company: '',
      customer_address: '',
      customer_gstin: '',
      customer_pan: ''
    }));
  };

  const handleContactChange = (contactId) => {
    if (!contactType || !contactId) return;

    if (contactType === 'supplier') {
      const supplier = suppliers.find(s => s.id === parseInt(contactId));
      if (supplier) {
        const details = extractSupplierDetails(supplier);
        setFormData(prev => ({
          ...prev,
          customer: contactId,
          customer_company: details.company,
          customer_address: details.address,
          customer_gstin: details.gstin,
          customer_pan: details.pan
        }));
      }
    } else if (contactType === 'customer') {
      const customer = customers.find(c => c.id === parseInt(contactId));
      if (customer) {
        const details = extractCustomerDetails(customer);
        setFormData(prev => ({
          ...prev,
          customer: contactId,
          customer_name: details.name,
          customer_company: details.company,
          customer_address: details.address,
          customer_gstin: details.gstin,
          customer_pan: details.pan
        }));
      }
    } else if (contactType === 'client') {
      const client = clients.find(c => c.id === parseInt(contactId));
      if (client) {
        const details = extractClientDetails(client);
        setFormData(prev => ({
          ...prev,
          customer: contactId,
          customer_name: details.name,
          customer_company: details.company,
          customer_address: details.address,
          customer_gstin: details.gstin,
          customer_pan: details.pan
        }));
      }
    }
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === parseInt(customerId));
    if (customer) {
      const details = extractCustomerDetails(customer);
      setFormData(prev => ({
        ...prev,
        customer: customerId,
        customer_name: details.name,
        customer_company: details.company,
        customer_address: details.address,
        customer_gstin: details.gstin,
        customer_pan: details.pan
      }));
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    const numericFields = ['quantity', 'amount'];

    const resolveCatalogMeta = (catalogItem) => {
      const catalogData = useSuppliers
        ? (catalogItem.purchased_data || {})
        : (catalogItem.catalog_data || catalogItem.goods_data || catalogItem.services_data || {});
      const isService = useSuppliers
        ? catalogItem.category_type === 'services'
        : !!(catalogData.service_name || catalogData.sac_code || catalogItem.type === 'service' || catalogItem.category_type === 'services');
      const isGoods = !isService;
      const itemType = isGoods ? 'goods' : 'service';
      const codeValue = isService ? (catalogData.sac_code || '') : (catalogData.hsn_code || '');
      const price = parseFloat(catalogData.unit_cost || catalogData.unit_price || catalogData.price || catalogItem.price || 0) || 0;
      const description = useSuppliers
        ? (catalogData.item_name || '')
        : (catalogData.goods_name || catalogData.service_name || catalogItem.description || '');
      return { itemType, codeValue, price, description };
    };

    newItems[index] = {
      ...newItems[index],
      item_type: newItems[index].item_type || 'goods',
      [field]: numericFields.includes(field) ? parseFloat(value) || 0 : value
    };
    
    // If description/item is selected, update from catalog
    if (field === 'description' && value) {
      const itemPool = useSuppliers ? purchasedItems : salesCatalogItems;
      const catalogItem = itemPool.find(item => {
        const catalogData = useSuppliers
          ? (item.purchased_data || {})
          : (item.catalog_data || item.goods_data || item.services_data || {});
        const itemName = useSuppliers
          ? (catalogData.item_name || '')
          : (catalogData.goods_name || catalogData.service_name || '');
        return itemName === value || item.id === parseInt(value);
      });
      
      if (catalogItem) {
        const { itemType, codeValue, price, description } = resolveCatalogMeta(catalogItem);
        newItems[index] = {
          ...newItems[index],
          item_type: itemType,
          description: description || value,
          sac_code: itemType === 'service' ? codeValue : '',
          hsn_code: itemType === 'goods' ? codeValue : '',
          amount: price,
          quantity: itemType === 'service' ? 1 : (newItems[index].quantity || 1)
        };
      }
    }
    
    // Calculate totals with new items
    const totalTaxableValue = newItems.reduce((sum, item) => {
      const quantity = item.item_type === 'service' ? 1 : (item.quantity || 1);
      return sum + (item.amount * quantity);
    }, 0);
    const useCgstSgst = shouldUseCgstSgst(companyProfile?.gstin, formData.customer_gstin);
    const taxRate = 18;
    
    let igstAmount = 0;
    let cgstAmount = 0;
    let sgstAmount = 0;
    
    if (useCgstSgst) {
      cgstAmount = (totalTaxableValue * (taxRate / 2)) / 100;
      sgstAmount = (totalTaxableValue * (taxRate / 2)) / 100;
    } else {
      igstAmount = (totalTaxableValue * taxRate) / 100;
    }
    
    const totalInvoiceValue = totalTaxableValue + igstAmount + cgstAmount + sgstAmount;
    
    // Update all at once
    setFormData(prev => ({
      ...prev,
      items: newItems,
      total_taxable_value: totalTaxableValue,
      igst_rate: useCgstSgst ? 0 : taxRate,
      igst_amount: igstAmount,
      cgst_rate: useCgstSgst ? taxRate / 2 : 0,
      cgst_amount: cgstAmount,
      sgst_rate: useCgstSgst ? taxRate / 2 : 0,
      sgst_amount: sgstAmount,
      total_invoice_value: totalInvoiceValue
    }));
  };

  const calculateTotals = (items) => {
    const totalTaxableValue = items.reduce((sum, item) => {
      const quantity = item.item_type === 'service' ? 1 : (item.quantity || 1);
      return sum + (item.amount * quantity);
    }, 0);
    
    setFormData((prev) => {
      const useCgstSgst = shouldUseCgstSgst(companyProfile?.gstin, prev.customer_gstin);
      const taxRate = 18; // Default 18%
      let igstAmount = 0;
      let cgstAmount = 0;
      let sgstAmount = 0;
      if (useCgstSgst) {
        cgstAmount = (totalTaxableValue * (taxRate / 2)) / 100;
        sgstAmount = (totalTaxableValue * (taxRate / 2)) / 100;
      } else {
        igstAmount = (totalTaxableValue * taxRate) / 100;
      }
      const totalInvoiceValue = totalTaxableValue + igstAmount + cgstAmount + sgstAmount;
      return {
        ...prev,
        total_taxable_value: totalTaxableValue,
        igst_rate: useCgstSgst ? 0 : taxRate,
        igst_amount: igstAmount,
        cgst_rate: useCgstSgst ? taxRate / 2 : 0,
        cgst_amount: cgstAmount,
        sgst_rate: useCgstSgst ? taxRate / 2 : 0,
        sgst_amount: sgstAmount,
        total_invoice_value: totalInvoiceValue
      };
    });
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: prev.items.length + 1,
          sac_code: '',
          hsn_code: '',
          item_type: 'goods',
          description: '',
          quantity: 1,
          amount: 0
        }
      ]
    }));
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      items: newItems
    }));
    calculateTotals(newItems);
  };

  const numberToWords = (num) => {
    if (num === 0) return 'Zero';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const convertHundreds = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) {
        return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      }
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertHundreds(n % 100) : '');
    };
    
    const convert = (n) => {
      if (n === 0) return '';
      if (n < 100) return convertHundreds(n);
      if (n < 1000) return convertHundreds(Math.floor(n / 100)) + ' Hundred' + (n % 100 ? ' ' + convertHundreds(n % 100) : '');
      if (n < 100000) {
        const thousands = Math.floor(n / 1000);
        const remainder = n % 1000;
        return convert(thousands) + ' Thousand' + (remainder ? ' ' + convertHundreds(remainder) : '');
      }
      if (n < 10000000) {
        const lakhs = Math.floor(n / 100000);
        const remainder = n % 100000;
        return convert(lakhs) + ' Lakh' + (remainder ? ' ' + convert(remainder) : '');
      }
      const crores = Math.floor(n / 10000000);
      const remainder = n % 10000000;
      return convert(crores) + ' Crore' + (remainder ? ' ' + convert(remainder) : '');
    };
    
    const integerPart = Math.floor(num);
    const decimalPart = Math.round((num - integerPart) * 100);
    
    let result = convert(integerPart);
    if (decimalPart > 0) {
      result += ' and ' + convertHundreds(decimalPart) + ' Paise';
    }
    
    return result || 'Zero';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      // Map document types to API endpoints and payload structure
      const documentTypeMap = {
        'domestic-invoice': { endpoint: API_ENDPOINTS.INVOICES_CREATE, type: 'domestic' },
        'international-invoice': { endpoint: API_ENDPOINTS.INVOICES_CREATE, type: 'international' },
        'quote': { endpoint: API_ENDPOINTS.QUOTES_CREATE, type: 'quote' },
        'estimate': { endpoint: API_ENDPOINTS.ESTIMATES_CREATE, type: 'domestic' },
        'bill-of-supply': { endpoint: API_ENDPOINTS.BILLS_OF_SUPPLY_LIST, type: 'bill_of_supply' },
        'credit-note': { endpoint: API_ENDPOINTS.CREDIT_NOTES_LIST, type: 'credit_note' },
        'receipt': { endpoint: API_ENDPOINTS.RECEIPTS_LIST, type: 'receipt' },
        'delivery-challan': { endpoint: API_ENDPOINTS.DELIVERY_CHALLANS_LIST, type: 'delivery_challan' },
        'purchase-invoice': { endpoint: API_ENDPOINTS.PURCHASE_INVOICES_LIST, type: 'purchase_invoice' },
        'purchase-order': { endpoint: API_ENDPOINTS.PURCHASE_ORDERS_CREATE, type: 'purchase_order' },
        'debit-note': { endpoint: API_ENDPOINTS.DEBIT_NOTES_CREATE, type: 'debit_note' },
        'voucher-payment': { endpoint: API_ENDPOINTS.VOUCHER_PAYMENTS_CREATE, type: 'voucher_payment' },
        'salary-payment': { endpoint: API_ENDPOINTS.SALARY_PAYMENTS_CREATE, type: 'salary_payment' }
      };

      const docConfig = documentTypeMap[invoiceType] || documentTypeMap['domestic-invoice'];

      // Salary payment: employee, payroll-style fields, no items
      if (useSalaryPayment) {
        if (!salaryForm.employee) {
          setErrorMessage('Please select an employee');
          setIsSubmitting(false);
          return;
        }
        const gross = parseFloat(salaryForm.gross_salary) || 0;
        const hra = parseFloat(salaryForm.house_rent_allowance) || 0;
        const transport = parseFloat(salaryForm.transport_allowance) || 0;
        const medical = parseFloat(salaryForm.medical_allowance) || 0;
        const othersAllowance = hra + transport + medical;
        const incomeTax = parseFloat(salaryForm.income_tax) || 0;
        const pf = parseFloat(salaryForm.provident_fund) || 0;
        const pt = parseFloat(salaryForm.professional_tax) || 0;
        const netPay = parseFloat(salaryForm.net_pay) || 0;
        const amount = gross || (parseFloat(salaryForm.basic_salary) || 0) + othersAllowance;
        const payload = {
          employee: parseInt(salaryForm.employee),
          payment_date: salaryForm.payment_date || new Date().toISOString().split('T')[0],
          salary_month: salaryForm.salary_month || salaryForm.payment_date,
          payment_mode: salaryForm.payment_mode || 'bank_transfer',
          amount: toMoney(amount),
          tds_deduction: toMoney(incomeTax),
          professional_tax: toMoney(pt),
          employee_pf: toMoney(pf),
          employer_pf: '0.00',
          employee_esi: '0.00',
          employer_esi: '0.00',
          labour_welfare_fund: '0.00',
          others: toMoney(othersAllowance),
          net_pay: toMoney(netPay),
          ctc: toMoney(amount),
          authorized_signatory: null,
          notes: salaryForm.notes || '',
          status: salaryForm.status || 'draft'
        };
        const response = await axios.post(docConfig.endpoint, payload, { headers });
        onSave(response.data);
        onClose();
        return;
      }
      
      // For quotes, use the quote-specific payload structure
      if (invoiceType === 'quote') {
        // Validate required fields for quotes
        if (!formData.due_date) {
          setErrorMessage('Due date is required for quotes');
          setIsSubmitting(false);
          return;
        }

        if (!formData.customer || !contactType) {
          setErrorMessage('Please select a customer or client for the quote');
          setIsSubmitting(false);
          return;
        }

        // Format monetary fields with 2 decimals
        const subtotalMoney = toMoney(formData.total_taxable_value);
        const taxMoney = toMoney(formData.igst_amount + formData.cgst_amount + formData.sgst_amount);
        const totalMoney = toMoney(formData.total_invoice_value);

        // Validate max length (15 digits total)
        if (exceedsMaxDigits(subtotalMoney) || exceedsMaxDigits(taxMoney) || exceedsMaxDigits(totalMoney)) {
          setErrorMessage('Amounts must not exceed 15 digits in total.');
          setIsSubmitting(false);
          return;
        }

        // Line items — same shape as invoices / bill-of-supply; backend must persist these on create
        const quoteItems = formData.items.map((item) => {
          const itemAmount = item.amount * (item.item_type === 'service' ? 1 : item.quantity);
          const useCgstSgst = formData.igst_amount === 0 && (formData.cgst_amount > 0 || formData.sgst_amount > 0);
          const taxRate = useCgstSgst ? formData.cgst_rate * 2 : formData.igst_rate;
          const taxAmount = (itemAmount * taxRate) / 100;
          return {
            sac_code: item.sac_code,
            hsn_code: item.hsn_code,
            description: item.description,
            quantity: item.item_type === 'service' ? 1 : item.quantity,
            rate: item.amount,
            amount: itemAmount,
            tax_rate: taxRate,
            tax_amount: taxAmount
          };
        });

        const hasDescribedLine = quoteItems.some((row) => String(row.description || '').trim());
        if (!hasDescribedLine) {
          setErrorMessage('Add at least one line item with a description');
          setIsSubmitting(false);
          return;
        }

        // Build payload with customer or client based on contact type
        const payload = {
          invoice_number: formData.invoice_number || null,
          customer: contactType === 'customer' ? parseInt(formData.customer) : undefined,
          client: contactType === 'client' ? parseInt(formData.customer) : undefined,
          invoice_date: formData.invoice_date,
          due_date: formData.due_date,
          subtotal: subtotalMoney,
          tax_amount: taxMoney,
          total_amount: totalMoney,
          status: 'draft',
          notes: formData.notes,
          terms: formData.terms || formData.notes || '',
          items: quoteItems
        };

        const response = await axios.post(
          docConfig.endpoint,
          payload,
          { headers }
        );

        onSave(response.data);
        onClose();
        return;
      }

      // For receipts, map to receipts payload structure
      if (invoiceType === 'receipt') {
        const selectedContactType = contactType || (formData.customer ? 'customer' : '');
        const hasContact = !!formData.customer;
        const hasInvoice = formData.invoice;
        if (!hasContact && !hasInvoice) {
          setErrorMessage('Please select a customer/client or provide an invoice ID');
          setIsSubmitting(false);
          return;
        }
        const paymentDate = formData.invoice_date || new Date().toISOString().split('T')[0];
        const amount = toMoney(formData.total_invoice_value);

        const payload = {
          receipt_number: formData.invoice_number || null,
          payment_date: paymentDate,
          payment_type: formData.payment_type || 'bank_transfer',
          amount,
          reference_number: formData.reference_number || '',
          notes: formData.notes,
          status: formData.status || 'draft',
          invoice: formData.invoice ? parseInt(formData.invoice) : null,
          customer: hasContact && selectedContactType !== 'client' ? parseInt(formData.customer) : undefined,
          client: hasContact && selectedContactType === 'client' ? parseInt(formData.customer) : undefined
        };

        const response = await axios.post(
          docConfig.endpoint,
          payload,
          { headers }
        );

        onSave(response.data);
        onClose();
        return;
      }

      // Purchase documents: supplier, subtotal, tax_amount, total_amount (or amount for voucher)
      if (useSuppliers) {
        if (!formData.customer) {
          setErrorMessage('Please select a supplier');
          setIsSubmitting(false);
          return;
        }
        const subtotalMoney = toMoney(formData.total_taxable_value);
        const taxMoney = toMoney(formData.igst_amount + formData.cgst_amount + formData.sgst_amount);
        const totalMoney = toMoney(formData.total_invoice_value);
        if (exceedsMaxDigits(subtotalMoney) || exceedsMaxDigits(taxMoney) || exceedsMaxDigits(totalMoney)) {
          setErrorMessage('Amounts must not exceed 15 digits in total.');
          setIsSubmitting(false);
          return;
        }
        if (invoiceType === 'voucher-payment') {
          const payload = {
            supplier: parseInt(formData.customer),
            payment_date: formData.invoice_date || new Date().toISOString().split('T')[0],
            payment_type: formData.payment_type || 'bank_transfer',
            amount: totalMoney,
            reference_number: formData.reference_number || null,
            notes: formData.notes,
            status: formData.status || 'draft'
          };
          const response = await axios.post(docConfig.endpoint, payload, { headers });
          onSave(response.data);
          onClose();
          return;
        }
        const purchasePayload = {
          supplier: parseInt(formData.customer),
          invoice_date: formData.invoice_date || null,
          due_date: formData.due_date || formData.invoice_date || null,
          subtotal: subtotalMoney,
          tax_amount: taxMoney,
          total_amount: totalMoney,
          status: 'draft',
          type: invoiceType === 'purchase-invoice' ? 'purchase_invoice' : invoiceType === 'debit-note' ? 'debit_note' : 'purchase_order',
          notes: formData.notes,
          terms: formData.terms || formData.notes || '',
          specialise_supply: false,
          apply_reverse_charge: false,
          claim_itc: true,
          items: formData.items.map(item => {
            const qty = item.item_type === 'service' ? 1 : (item.quantity || 1);
            const lineAmount = item.amount * qty;
            const useCgstSgst = formData.igst_amount === 0 && (formData.cgst_amount > 0 || formData.sgst_amount > 0);
            const taxRate = useCgstSgst ? formData.cgst_rate * 2 : formData.igst_rate;
            return {
              description: item.description,
              quantity: parseFloat(qty).toFixed(2),
              rate: parseFloat(item.amount).toFixed(2),
              amount: parseFloat(lineAmount).toFixed(2),
              tax_rate: parseFloat(taxRate || 0).toFixed(2),
              tax_amount: parseFloat((lineAmount * (taxRate || 0)) / 100).toFixed(2),
              hsn_code: item.hsn_code || '',
              sac_code: item.sac_code || ''
            };
          })
        };
        const response = await axios.post(docConfig.endpoint, purchasePayload, { headers });
        onSave(response.data);
        onClose();
        return;
      }
      
      // Validate contact selection for invoices/other docs
      if (!formData.customer || !contactType) {
        setErrorMessage('Please select a customer or client');
        setIsSubmitting(false);
        return;
      }
      
      // For other document types, use the invoice payload structure
      const resolvedDueDate =
        documentConfig.showDueDate && formData.due_date
          ? formData.due_date
          : formData.due_date || formData.invoice_date;

      if (invoiceType === 'bill-of-supply' && !resolvedDueDate) {
        setErrorMessage('Due date is required for Bill of Supply.');
        setIsSubmitting(false);
        return;
      }

      const payload = {
        invoice_number: formData.invoice_number || null,
        type: docConfig.type,
        status: 'draft',
        invoice_date: formData.invoice_date,
        // ensure due_date is always present to satisfy backend constraint
        due_date: resolvedDueDate,
        subtotal: formData.total_taxable_value,
        tax_amount: formData.igst_amount + formData.cgst_amount + formData.sgst_amount,
        total_amount: formData.total_invoice_value,
        notes: formData.notes,
        terms: formData.terms || formData.notes || '',
        template_type: 'inline',
        adjustment_type: 'discount',
        adjustment_amount: 0,
        ...(invoiceType !== 'estimate'
          ? { document_type: invoiceType }
          : {}),
        items: formData.items.map(item => {
          const itemAmount = item.amount * (item.item_type === 'service' ? 1 : item.quantity);
          const useCgstSgst = formData.igst_amount === 0 && (formData.cgst_amount > 0 || formData.sgst_amount > 0);
          const taxRate = useCgstSgst ? formData.cgst_rate * 2 : formData.igst_rate;
          const taxAmount = (itemAmount * taxRate) / 100;
          return {
            sac_code: item.sac_code,
            hsn_code: item.hsn_code,
            description: item.description,
            quantity: item.item_type === 'service' ? 1 : item.quantity,
            rate: item.amount,
            amount: itemAmount,
            tax_rate: taxRate,
            tax_amount: taxAmount
          };
        })
      };

      // Assign customer/client based on selected contact type
      if (contactType === 'customer') {
        payload.customer = parseInt(formData.customer);
      } else if (contactType === 'client') {
        payload.client = parseInt(formData.customer);
      }

      const response = await axios.post(
        docConfig.endpoint,
        payload,
        { headers }
      );

      onSave(response.data);
      onClose();
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      `Failed to create ${documentConfig.banner.toLowerCase()}`;
      setErrorMessage(apiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .tax-invoice-form-scroll::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }
        .tax-invoice-form-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .tax-invoice-form-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .tax-invoice-form-scroll::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .tax-invoice-table-scroll::-webkit-scrollbar {
          height: 8px;
        }
        .tax-invoice-table-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .tax-invoice-table-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .tax-invoice-table-scroll::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-2 sm:p-4 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] flex flex-col my-4 sm:my-8 relative overflow-hidden">
          <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0 sticky top-0 z-[100] rounded-t-lg shadow-sm">
            <h2 className="text-lg sm:text-xl font-semibold text-foreground">{documentConfig.title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 relative z-[101] flex-shrink-0"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1 tax-invoice-form-scroll" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
              {errorMessage}
            </div>
          )}

          {/* Company Header */}
          <div className="border-b-2 border-gray-300 pb-4">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
              <div className="flex-1 w-full">
                <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
                  {companyProfile?.name || 'COMPANY NAME'}
                </h1>
                <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                  <p>
                    Address:{' '}
                    {companyProfile?.address || 'Company Address Line 1, City, State, PIN'}
                  </p>
                  <p className="break-words">
                    Mobile: {companyProfile?.phone || '+91 1234567890'} | Email:{' '}
                    {companyProfile?.email || 'info@company.com'}
                  </p>
                  <p className="break-words">
                    GSTIN: {companyProfile?.gstin || '29XXXXX1234X1XX'}
                  </p>
                </div>
              </div>
              <div className="text-left sm:text-right w-full sm:w-auto">
                {companyProfile?.logoUrl ? (
                  <img
                    src={companyProfile.logoUrl}
                    alt={companyProfile.name || 'Company Logo'}
                    className="h-12 sm:h-16 object-contain ml-auto"
                  />
                ) : (
                  <div className="text-base sm:text-lg font-semibold text-primary">LOGO</div>
                )}
              </div>
            </div>
          </div>

          {/* Document Type Banner */}
          <div className="bg-foreground text-white text-center py-2 sm:py-3 font-bold text-base sm:text-lg">
            {documentConfig.banner}
          </div>

          {useSalaryPayment ? (
            /* Salary Payment Form - Employee & Payroll fields, no items */
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employee <span className="text-red-500">*</span></label>
                    <select
                      value={salaryForm.employee}
                      onChange={(e) => setSalaryForm(p => ({ ...p, employee: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select Employee</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {getEmployeeDisplayName(emp)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                    <input
                      type="date"
                      value={salaryForm.payment_date}
                      onChange={(e) => setSalaryForm(p => ({ ...p, payment_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Salary Month</label>
                    <input
                      type="date"
                      value={salaryForm.salary_month}
                      onChange={(e) => setSalaryForm(p => ({ ...p, salary_month: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="YYYY-MM-01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
                    <select
                      value={salaryForm.payment_mode}
                      onChange={(e) => setSalaryForm(p => ({ ...p, payment_mode: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cash">Cash</option>
                      <option value="cheque">Cheque</option>
                      <option value="payable">Payable</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={salaryForm.status}
                      onChange={(e) => setSalaryForm(p => ({ ...p, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Salary Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gross Salary</label>
                    <input
                      type="number"
                      step="0.01"
                      value={salaryForm.gross_salary}
                      onChange={(e) => setSalaryForm(p => ({ ...p, gross_salary: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Basic Salary</label>
                    <input
                      type="number"
                      step="0.01"
                      value={salaryForm.basic_salary}
                      onChange={(e) => setSalaryForm(p => ({ ...p, basic_salary: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Net Pay</label>
                    <input
                      type="number"
                      step="0.01"
                      value={salaryForm.net_pay}
                      onChange={(e) => setSalaryForm(p => ({ ...p, net_pay: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Allowances</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">House Rent Allowance</label>
                    <input
                      type="number"
                      step="0.01"
                      value={salaryForm.house_rent_allowance}
                      onChange={(e) => setSalaryForm(p => ({ ...p, house_rent_allowance: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transport Allowance</label>
                    <input
                      type="number"
                      step="0.01"
                      value={salaryForm.transport_allowance}
                      onChange={(e) => setSalaryForm(p => ({ ...p, transport_allowance: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Medical Allowance</label>
                    <input
                      type="number"
                      step="0.01"
                      value={salaryForm.medical_allowance}
                      onChange={(e) => setSalaryForm(p => ({ ...p, medical_allowance: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Deductions</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Income Tax / TDS</label>
                    <input
                      type="number"
                      step="0.01"
                      value={salaryForm.income_tax}
                      onChange={(e) => setSalaryForm(p => ({ ...p, income_tax: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Provident Fund</label>
                    <input
                      type="number"
                      step="0.01"
                      value={salaryForm.provident_fund}
                      onChange={(e) => setSalaryForm(p => ({ ...p, provident_fund: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Professional Tax</label>
                    <input
                      type="number"
                      step="0.01"
                      value={salaryForm.professional_tax}
                      onChange={(e) => setSalaryForm(p => ({ ...p, professional_tax: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Bank Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                    <input
                      type="text"
                      value={salaryForm.account_number}
                      onChange={(e) => setSalaryForm(p => ({ ...p, account_number: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code</label>
                    <input
                      type="text"
                      value={salaryForm.ifsc_code}
                      onChange={(e) => setSalaryForm(p => ({ ...p, ifsc_code: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Optional"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={salaryForm.notes}
                  onChange={(e) => setSalaryForm(p => ({ ...p, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Additional notes..."
                />
              </div>
            </div>
          ) : (
          <>
          {/* Invoice Details and Bill To Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Document Details - Right Side */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{documentConfig.numberLabel}</label>
                <input
                  type="text"
                  value={formData.invoice_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{documentConfig.dateLabel}</label>
                <div className="relative">
                  <input
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              {documentConfig.showDueDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              )}
            </div>

            {/* Bill To / Supplier - Left Side */}
            <div className="space-y-4">
              {useSuppliers ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                  <select
                    value={formData.customer}
                    onChange={(e) => handleContactChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select Supplier</option>
                    {suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {getSupplierDisplayName(supplier)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Type</label>
                    <select
                      value={contactType}
                      onChange={(e) => handleContactTypeChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select Type</option>
                      <option value="customer">Customer</option>
                      <option value="client">Client</option>
                    </select>
                  </div>
                  {contactType && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {contactType === 'customer' ? 'Select Customer' : 'Select Client'}
                      </label>
                      <select
                        value={formData.customer}
                        onChange={(e) => handleContactChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Select {contactType === 'customer' ? 'Customer' : 'Client'}</option>
                        {contactType === 'customer' ? (
                          customers.map((customer) => {
                            const name = getCustomerDisplayName(customer);
                            return (
                              <option key={customer.id} value={customer.id}>
                                {name}
                              </option>
                            );
                          })
                        ) : (
                          clients.map((client) => (
                            <option key={client.id} value={client.id}>
                              {client.name || client.client_name || client.company_name || `Client ${client.id}`}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  )}
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <input
                  type="text"
                  value={formData.customer_company}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_company: e.target.value }))}
                  readOnly={useSuppliers && !!formData.customer}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${useSuppliers && formData.customer ? 'bg-gray-50' : ''}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  value={formData.customer_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_address: e.target.value }))}
                  readOnly={useSuppliers && !!formData.customer}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${useSuppliers && formData.customer ? 'bg-gray-50' : ''}`}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                  <input
                    type="text"
                    value={formData.customer_gstin}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_gstin: e.target.value }))}
                    readOnly={useSuppliers && !!formData.customer}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${useSuppliers && formData.customer ? 'bg-gray-50' : ''}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
                  <input
                    type="text"
                    value={formData.customer_pan}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_pan: e.target.value }))}
                    readOnly={useSuppliers && !!formData.customer}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${useSuppliers && formData.customer ? 'bg-gray-50' : ''}`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Itemized Services/Items Table */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">
              {invoiceType === 'delivery-challan' ? 'Items' : 'Services'}
            </h3>
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 tax-invoice-table-scroll" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-visible">
                  <table className="min-w-[640px] sm:min-w-full w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">S.No</th>
                        <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">
                          SAC / HSN Code
                        </th>
                        <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium min-w-[150px]">
                          Description of Goods / Services
                        </th>
                        <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">Quantity</th>
                        <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">Amount (₹)</th>
                        <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.items.map((item, index) => (
                        <tr key={item.id}>
                          <td className="border border-gray-300 px-2 sm:px-4 py-2 text-xs sm:text-sm">{index + 1}</td>
                          <td className="border border-gray-300 px-2 sm:px-4 py-2">
                            <input
                              type="text"
                              value={item.item_type === 'service' ? item.sac_code : item.hsn_code}
                              onChange={(e) => handleItemChange(index, item.item_type === 'service' ? 'sac_code' : 'hsn_code', e.target.value)}
                              className="w-full min-w-[80px] px-2 py-1 text-xs sm:text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                              placeholder={item.item_type === 'service' ? 'SAC Code' : 'HSN Code'}
                            />
                          </td>
                          <td className="border border-gray-300 px-2 sm:px-4 py-2">
                            <select
                              value={item.description}
                              onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                              className="w-full min-w-[150px] px-2 py-1 text-xs sm:text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                            >
                              <option value="">Select Item</option>
                            {(useSuppliers ? purchasedItems : salesCatalogItems).map((catalogItem) => {
                              const catalogData = useSuppliers
                                ? (catalogItem.purchased_data || {})
                                : (catalogItem.catalog_data || catalogItem.goods_data || catalogItem.services_data || {});
                              const itemName = useSuppliers
                                ? (catalogData.item_name || '')
                                : (catalogData.goods_name || catalogData.service_name || '');
                              const isService = useSuppliers
                                ? catalogItem.category_type === 'services'
                                : !!(catalogData.service_name || catalogData.sac_code || catalogItem.type === 'service');
                              const labelSuffix = isService ? ' (Service)' : ' (Goods)';
                              if (!itemName) return null;
                              return (
                                <option key={catalogItem.id} value={itemName}>
                                    {itemName}{labelSuffix}
                                </option>
                              );
                            })}
                            </select>
                          </td>
                      <td className="border border-gray-300 px-2 sm:px-4 py-2">
                        {item.item_type === 'service' ? (
                          <span className="text-xs sm:text-sm text-gray-500">Not required</span>
                        ) : (
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            min="1"
                            className="w-full min-w-[60px] px-2 py-1 text-xs sm:text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                        )}
                      </td>
                          <td className="border border-gray-300 px-2 sm:px-4 py-2">
                            <input
                              type="number"
                              value={item.amount}
                              onChange={(e) => handleItemChange(index, 'amount', e.target.value)}
                              min="0"
                              step="0.01"
                              className="w-full min-w-[80px] px-2 py-1 text-xs sm:text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          <div className="text-[11px] sm:text-xs text-gray-500 mt-1">
                            Line total: ₹{(item.amount * (item.item_type === 'service' ? 1 : (item.quantity || 1))).toFixed(2)}
                          </div>
                          </td>
                          <td className="border border-gray-300 px-2 sm:px-4 py-2">
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Remove"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={addItem}
              className="mt-3 sm:mt-4 w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>

          {/* Amount Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200">
                <div className="space-y-2">
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-xs sm:text-sm text-gray-700">Total Taxable Value:</span>
                    <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">₹{formData.total_taxable_value.toFixed(2)}</span>
                  </div>
                  {/* Show taxes only for invoices and bill of supply - IGST when different states, CGST+SGST when same state */}
                  {(invoiceType === 'domestic-invoice' || invoiceType === 'international-invoice' || invoiceType === 'bill-of-supply') && (
                    <>
                      {formData.igst_amount > 0 ? (
                        <div className="flex justify-between items-center gap-2">
                          <span className="text-xs sm:text-sm text-gray-700">IGST @ {formData.igst_rate}%:</span>
                          <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">₹{formData.igst_amount.toFixed(2)}</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between items-center gap-2">
                            <span className="text-xs sm:text-sm text-gray-700">CGST @ {formData.cgst_rate}%:</span>
                            <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">₹{formData.cgst_amount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center gap-2">
                            <span className="text-xs sm:text-sm text-gray-700">SGST @ {formData.sgst_rate}%:</span>
                            <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">₹{formData.sgst_amount.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                  <div className="flex justify-between items-center gap-2 pt-2 border-t border-gray-300">
                    <span className="text-sm sm:text-lg font-semibold">
                      {invoiceType === 'quote' || invoiceType === 'estimate' ? 'Total Amount' : 
                       invoiceType === 'receipt' ? 'Total Received' :
                       invoiceType === 'delivery-challan' ? 'Total Value' :
                       'Total Invoice Value'}:
                    </span>
                    <span className="text-sm sm:text-lg font-semibold whitespace-nowrap">₹{formData.total_invoice_value.toFixed(2)}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-300">
                    <p className="text-xs sm:text-sm text-gray-600 break-words">
                      {invoiceType === 'quote' || invoiceType === 'estimate' ? 'Total Amount' : 
                       invoiceType === 'receipt' ? 'Total Received' :
                       invoiceType === 'delivery-challan' ? 'Total Value' :
                       'Total Invoice Value'} (in words): {numberToWords(Math.floor(formData.total_invoice_value))} Rupees Only
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Show bank details for invoices, receipts, and bill of supply */}
              {(invoiceType === 'domestic-invoice' || invoiceType === 'international-invoice' || 
                invoiceType === 'bill-of-supply' || invoiceType === 'receipt' || invoiceType === 'estimate') && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Bank Details for Payment</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <label className="text-gray-600 min-w-[100px] sm:min-w-[120px]">Account Name:</label>
                      <span className="flex-1 text-foreground break-words">
                        {companyProfile?.accountName || formData.bank_account_name || '—'}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <label className="text-gray-600 min-w-[100px] sm:min-w-[120px]">Bank Name:</label>
                      <span className="flex-1 text-foreground break-words">
                        {companyProfile?.bankName || formData.bank_name || '—'}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <label className="text-gray-600 min-w-[100px] sm:min-w-[120px]">Account No:</label>
                      <span className="flex-1 text-foreground break-words">
                        {companyProfile?.accountNo || formData.bank_account_no || '—'}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <label className="text-gray-600 min-w-[100px] sm:min-w-[120px]">IFSC Code:</label>
                      <span className="flex-1 text-foreground break-words">
                        {companyProfile?.ifscCode || formData.bank_ifsc || '—'}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <label className="text-gray-600 min-w-[100px] sm:min-w-[120px]">Branch:</label>
                      <span className="flex-1 text-foreground break-words">
                        {companyProfile?.branch || formData.bank_branch || '—'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {/* Show signature for all document types */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Signature</h3>
                <div className="text-sm text-gray-600">
                  <p>For {companyProfile?.name || 'COMPANY NAME'}</p>
                  <div className="mt-8 border-t border-gray-300 pt-2">
                    <p>Authorized Signatory</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Additional notes..."
            />
          </div>

          {/* Terms - Only for quotes */}
          {invoiceType === 'quote' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Terms</label>
              <textarea
                value={formData.terms}
                onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Payment terms, validity period, etc."
              />
            </div>
          )}

          </>
          )}

          {/* Action Buttons */}
          <div className="flex justify-center pt-4 border-t border-gray-200">
            <Button type="submit" disabled={isSubmitting} size="lg" className="w-full sm:w-auto">
              {isSubmitting ? 'Creating...' : documentConfig.submitText}
            </Button>
          </div>
        </form>
        </div>
      </div>
    </>
  );
};

export default TaxInvoiceForm;

